import {
  setUserName,
  setProfileFields,
  setProfilePicture as setProfilePictureRedux,
} from '../redux/slices/applicationSlice';
import { store } from '../redux/store';
import { EmailPreferenceType } from '../types/misc';
import { ProfileField, UserStats } from '../types/profile';
import { RootState } from '../types/redux';
import { CheckEmailRequest } from '../types/requests';
import { CheckEmailResponse } from '../types/responses';
import httpService from './httpService';

export function getUserData(): Promise<ProfileField[]> {
  return httpService.get<ProfileField[]>('/properties/@me').then(({ data }): ProfileField[] => {
    store.dispatch(setProfileFields(data));
    return data;
  });
}

export function addFields(field: ProfileField[], includeAll = false): Promise<ProfileField[]> {
  if (!field.length) return Promise.resolve([]);
  return httpService.post<ProfileField[]>('/properties/@me', field).then(({ data }): ProfileField[] => {
    const difference = data.filter(
      (nf) => !(store.getState() as RootState).application.profileFields?.map((f) => f.id).includes(nf.id),
    );
    store.dispatch(setProfileFields(data));
    return includeAll ? data : difference;
  });
}

export async function updateField(field: ProfileField): Promise<ProfileField[]> {
  return httpService.put<ProfileField[]>(`/properties/@me/${field.id}`, field).then(({ data }): ProfileField[] => {
    store.dispatch(setProfileFields(data));
    return data;
  });
}

export async function checkEmailExists(email: string): Promise<boolean> {
  const body: CheckEmailRequest = { email };

  return httpService.post<CheckEmailResponse>('/auth/email/status', body).then(({ data }): boolean => data.hasAccount);
}

export async function getUserStatistics(): Promise<UserStats> {
  return httpService.get<UserStats>('/users/@me/statistics').then(({ data }): UserStats => data);
}

export async function getUserPreferences(): Promise<EmailPreferenceType[]> {
  return httpService.get<any>('/notifications/preferences').then(({ data }): EmailPreferenceType[] => {
    if (Array.isArray(data)) return data as EmailPreferenceType[];
    if (data && Array.isArray((data as any).userPreferences)) return (data as any).userPreferences;
    return [];
  });
}

export async function updateUserPreferences(preferences: EmailPreferenceType[]): Promise<void> {
  return httpService.put('/notifications/preferences', preferences);
}

export function getProfilePicture(): Promise<string> {
  return httpService
    .get('/users/@me/avatar')
    .then(({ data }): string => {
      store.dispatch(setProfilePictureRedux(data.path));
      return data.path;
    })
    .catch(() => '');
}

export function setProfilePicture(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('picture', file);
  return httpService.post('/users/@me/avatar', formData).then(({ data }) => {
    store.dispatch(setProfilePictureRedux(data.path));
    return data.path;
  });
}

export function deleteProfilePicture(): Promise<void> {
  return httpService.delete('/users/@me/avatar').then(() => {
    store.dispatch(setProfilePictureRedux(undefined));
  });
}

export function deleteField(id: string): Promise<ProfileField[]> {
  return httpService.delete(`/properties/@me/${id}`).then(({ data }): ProfileField[] => {
    store.dispatch(setProfileFields(data));
    return data;
  });
}

export function deleteFields(ids: string[]): Promise<ProfileField[]> {
  return Promise.all(ids.map((id) => deleteField(id))).then((data) => data.flat());
}

export function changeName(newName: string): Promise<void> {
  const [firstName, lastName] = newName.split('+');
  newName = `${firstName.trim()}+${lastName.trim()}`;
  return httpService.post('/users/@me/name', { name: newName }).then(() => {
    store.dispatch(setUserName({ firstName: firstName.trim(), lastName: lastName.trim() }));
  });
}
